// Capabilities — decisões derivadas dos signals.
//
// Cada capability é função PURA de signals (eventualmente + settings).
// Caller consome capability, nunca signal direto. Mudar threshold de
// uma decisão = mudar lógica em 1 lugar (aqui), não em N callers.
//
// Regra: NUNCA expor tier ou signal cru pro caller. A semântica é
// "o que o app faz", não "qual é o hardware". Capability `animations`
// é mais útil que `cpuTier` pra um <EmptyState> decidir mostrar SVG.

import type { ChannelQuality, Tier } from '@app-iptv/shared'

import { lookupKnownModel } from './models'
import {
  canPlayNativeTs,
  canPrefetchVod,
  clampRankToBand,
  clampTierToBand,
  platformTierBand,
  rankOf,
} from './platform-tier'
import {
  getSignals,
  refreshNetworkSignal,
  subscribeNetwork,
  subscribeSignalsRefresh,
  type DeviceSignals,
} from './signals'

// ===== Tipos =====

export type AnimationLevel = 'off' | 'minimal' | 'full'
export type ImagePreset = 'small' | 'medium' | 'large'
export type BackdropPreset = 'sd' | 'hd' | 'fhd' | 'uhd'
export type PreferredImageFormat = 'avif' | 'webp' | 'jpeg'
export type PrefetchConcurrency = 1 | 2 | 4
export type RetryCount = 0 | 1
/** Estado da sessão long-running. Capabilities derivadas degradam
 *  progressivamente conforme avança. Spec em performance.md §9.5.
 *  - 'normal': < 3h, sem degradação.
 *  - 'warn': 3-6h, prefetch reduzido + animations cap em minimal.
 *  - 'degrade': 6-8h, hero autoplay off + livePreview off + episodeWindow menor.
 *  - 'suggest': 8h+, mostra prompt "Reiniciar app?" (UI). Capabilities
 *    iguais a 'degrade' (já está degradado o que dá pra degradar). */
export type LongSessionMode = 'normal' | 'warn' | 'degrade' | 'suggest'

export interface DeviceCapabilities {
  // ===== Animação / UI =====
  /** Nível geral de animações secundárias (transições, parallax, fade longo). */
  animations: AnimationLevel
  /** Hero rotativo gira automaticamente. */
  autoplayHero: boolean
  /** Intervalo entre slides do hero (ms). 0 quando autoplay off. */
  heroIntervalMs: number

  // ===== Player =====
  /** Hero ao vivo ataca preview de stream automaticamente (HLS). */
  livePreviewEnabled: boolean
  /** Qualidade default que o player tenta. 'auto' deixa player decidir. */
  defaultPlayerQuality: ChannelQuality | 'auto'
  /** Toca MPEG-TS (LIVE) no `<video>` NATIVO — sem mpegts.js/MSE. `true` SÓ na
   *  CLASSE provada em device (webOS gen 4-5, ver `canPlayNativeTs`). É uma
   *  capacidade de HARDWARE de mídia da plataforma, NÃO derivada do tier de
   *  perf — por isso escapa do override de tier do Settings. Consumida pelo
   *  `createPlayer`/`WebPlayer` (ramo native-ts, só pra `source.type==='mpegts'`)
   *  e pelo CSS do frame de vídeo (o punch-through de HW ignora o border-radius
   *  → canto reto no preview/PiP, ver `html[data-native-ts]`). */
  nativeTsPlayback: boolean
  /** PREFETCH do filme na ficha (`VodPrefetcher`): baixar o filme num `<video>`
   *  escondido enquanto a ficha está aberta. `false` = nenhum sign, nenhum
   *  `<video>`, nenhuma requisição. Default POR PLATAFORMA em `canPrefetchVod`
   *  (hoje `false` em todas, AP-161); o A/B liga/desliga pelo override
   *  `VOD_PREFETCH_OVERRIDE_KEY`. Decisão de CUSTO de rede, não de perf de UI: não
   *  sai do tier (o tier só escolhe `auto`/`metadata` quando ligado). */
  vodPrefetch: boolean

  // ===== Lazy mount / virtualização =====
  /** Janela inicial de episódios renderizados em SeriesDetails. */
  episodeInitialWindow: number
  /** Step de expansão quando user foca no fim. 0 = sem expansão (já tudo visível). */
  episodeExpandStep: number
  /** Tamanho da página do Catálogo (Filmes/Séries) em modo paginação
   *  cumulativa (D.0 do Catálogo v1, 2026-05-11). Cliente baixa a fatia
   *  inicial proporcional ao tier e busca mais conforme rola. Valores
   *  visam manter parse < 100ms + RAM peak < 5MB em vlow Tizen 2017. */
  catalogPageSize: number
  /** Overscan da lista virtual da TV ao Vivo — LINHAS extra montadas além do
   *  mínimo que já cobre o foco + vizinhos (a janela deslizante do
   *  `liveWindow.ts`). Reduz o blip de reciclagem em devices que aguentam.
   *  ADITIVO: só ALARGA a janela — nunca reduz abaixo do piso (back=visible,
   *  forward=visible+1), então o invariante de foco (a janela contém o foco e
   *  seus vizinhos) segue garantido. 0 em vlow/low (RAM apertada; mantém o
   *  comportamento já validado na rc11), 1 em mid, 2 em high/ultra. */
  liveListOverscan: number

  // ===== Network / RQ =====
  /** Quantas tentativas de retry após falha de fetch. */
  retryCount: RetryCount
  /** Delay entre tentativas (ms). */
  retryDelayMs: number
  /** Timeout default de fetch no client (ms). */
  fetchTimeoutMs: number
  /** gcTime do React Query (ms). */
  rqGcTimeMs: number

  // ===== Polling =====
  /** Refresh interval do EPG na LiveScreen (ms). 0 = polling off. */
  epgRefreshIntervalMs: number
  /** Refresh interval base do catálogo client-side (#8). Match com
   *  worker backend (30min) em rede saudável; estendido em network
   *  poor pra não competir com streaming. Hook adiciona jitter ±5min
   *  pra dispersar load. */
  catalogRefreshIntervalMs: number

  // ===== Cache =====
  /** maxEntries do SW image cache. Capa eviction LRU em low-end. */
  swImageMaxEntries: number
  /** Suffix do cache name no SW (`iptv-images-{suffix}`). Mantém compat
   *  com sw.ts atual que usa `tier`. */
  swImageCacheSuffix: string

  // ===== Imagens (combina viewport + features + storage) =====
  /** Preset de tamanho pra posters em cards. */
  imagePreset: ImagePreset
  /** Preset pra backdrops (tela cheia). */
  backdropPreset: BackdropPreset
  /** Formato preferido. Cliente usa em <picture> ou ?format= no proxy futuro. */
  preferredImageFormat: PreferredImageFormat

  // ===== Prefetch =====
  /** Quantos prefetches em paralelo (foco direcional, etc). */
  prefetchConcurrency: PrefetchConcurrency

  // ===== Long-session =====
  /** Timestamps (ms desde session start) pra disparar degradação. */
  longSessionThresholds: { warn: number; degrade: number; suggest: number }
  /** Estado atual da sessão. Recomputado periodicamente (timer no
   *  bootstrap chama `refreshCapabilities` a cada 5min). Capabilities
   *  upstream degradam baseadas neste valor. */
  longSessionMode: LongSessionMode

  // ===== Tier compat =====
  /** Tier conceitual do device — derivado dos signals pra retrocompat
   *  com APIs externas (createPlayer, X-Device-Tier header, SW cache key).
   *  NÃO usar em decisões internas — use a capability específica. */
  cpuTier: Tier
}

// ===== Override do user (Settings → Forçar tier) =====
//
// Override de tier força CPU tier conceitual; demais signals continuam
// reais. Uso típico: testing ("quero ver como roda em vlow") sem mexer
// em hardware. Capabilities derivam normalmente — backdrop continua FHD
// se viewport é FHD, mesmo com tier override 'vlow'.

const TIER_OVERRIDE_KEY = 'iptv:tier-override'

function readTierOverride(): Tier | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(TIER_OVERRIDE_KEY)
  if (raw && ['vlow', 'low', 'mid', 'high', 'ultra'].includes(raw)) {
    return raw as Tier
  }
  return null
}

// ===== Override do A/B do prefetch de filme (AP-161) =====
//
// SÓ pra medição. O default de produto é a tabela por plataforma
// (`canPrefetchVod`, hoje desligado em todas); este override existe porque sem
// ele não há como ligar o prefetch numa TV pra medir. Valores:
//   'on'  → liga (braço A do A/B);
//   'off' → desliga explicitamente (braço B, mesmo com a plataforma ligada);
//   ausente/outro → segue a plataforma.
// As capabilities são calculadas no boot e ficam em cache: depois de gravar a
// chave, RECARREGUE o app (`location.reload()`). No fim do A/B, apague a chave
// (`localStorage.removeItem('iptv:ab:vod-prefetch')`): ela sobrevive a reload e a
// atualização do app.
//
// Lido com try/catch: `localStorage` lança em modo privado, com storage bloqueado
// e em alguns WebViews. Storage que lança = sem override (default da plataforma),
// nunca capability quebrada.

export const VOD_PREFETCH_OVERRIDE_KEY = 'iptv:ab:vod-prefetch'

function readVodPrefetchOverride(): boolean | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(VOD_PREFETCH_OVERRIDE_KEY)
    if (raw === 'on') return true
    if (raw === 'off') return false
    return null
  } catch {
    return null
  }
}

// ===== Tier compat: signals → Tier conceitual (FONTE ÚNICA) =====
//
// Tier era um valor único. Capabilities são granulares, mas algumas APIs
// externas (X-Device-Tier header, SW cache key, createPlayer) ainda
// querem 1 valor. Esta é a ÚNICA fonte do tier conceitual do app — o
// `data-tier` do CSS, o `detectAutoTier()` do Settings e o header X-Device-Tier
// derivam TODOS daqui (ver tier.ts, que delega pra `deriveAutoTier`).
//
// **Precedência: PLATAFORMA > bench.** O microbench mede JS single-thread; o
// que trava TV velha é compositing de GPU (blur/shadow/transform) e memória,
// que o bench NÃO mede. A geração do motor (Chromium/webOS/Tizen) é o proxy
// determinístico e define a BANDA do tier (platform-tier.ts) — piso E teto, não
// só teto. Em TV CONHECIDA a plataforma DECIDE; o bench só desempata em device
// DESCONHECIDO (Android box genérico).
//
// Por que piso, e não só teto: o bench de JS de QUALQUER TV é lento. Medido na
// TV do dono (LG 2019, quad-core, 4K) com o bench deste repo, no device real:
// mediana 50ms ('low') e 94ms no boot frio ('vlow'). Como o webOS não expõe
// `deviceMemory` (ram.tier='unknown'), a CPU decidia SOZINHA — e um teto só puxa
// pra baixo, então nada consertava. Detalhe em `platform-tier.ts`.
//
// **Measured (dentro da banda): modelo conhecido, senão min(CPU, RAM).**
// min(CPU,RAM) cobre RAM-CPU descasados:
//   - VM/container com 16GB RAM (ultra) + 1 vCPU lento (vlow) → vlow
//   - Chromebook ARM com 8GB RAM (high) + SoC fraco (low) → low
//   - Mac M1 com 8GB RAM (high) + 8 cores rápidos (high) → high
// RAM 'unknown' (Chr38/53 sem deviceMemory) NÃO baixa nem infla: CPU decide o
// measured — mas a banda de plataforma DOMINA quando é TV conhecida.

const RANK_TO_TIER: Tier[] = ['vlow', 'low', 'mid', 'high', 'ultra']

function deriveTier(signals: DeviceSignals): Tier {
  const override = readTierOverride()
  if (override) return override
  return deriveAutoTier(signals)
}

/** Tier conceitual "natural" do device (IGNORA o override manual do Settings).
 *  Fonte única — `tier.ts::detectAutoTier` delega pra cá pra o valor mostrado no
 *  Settings casar 1:1 com o `data-tier` real do app. Pura: função de signals. */
export function deriveAutoTier(signals: DeviceSignals): Tier {
  // 1. Modelo conhecido (Fire TV `AFT*`, Nvidia Shield, Mi Box) = sinal EXATO;
  //    substitui o measured cpu/ram quando presente.
  const known = lookupKnownModel(signals.platform.model)

  // 2. Measured: pior de CPU × RAM. RAM 'unknown' → CPU decide sozinho.
  const cpuRank = rankOf(signals.cpu.tier)
  const ramRank = signals.ram.tier === 'unknown' ? cpuRank : rankOf(signals.ram.tier)
  const measuredRank = known !== null ? rankOf(known) : Math.min(cpuRank, ramRank)

  // 3. BANDA de PLATAFORMA/MOTOR — precedência sobre o bench, nos DOIS sentidos:
  //    - teto: TV 2017 (webOS 3 / Chromium 38) trava em vlow mesmo que o bench
  //      dê um burst rápido;
  //    - piso: TV 2018-2020 (webOS 4-5 / Chromium 53-68) não é rebaixada pra
  //      low/vlow pelo bench de JS, que numa TV é ruído (e no boot frio é pior
  //      ainda) e não mede o que de fato limita o device.
  const band = platformTierBand(signals.platform)
  return RANK_TO_TIER[clampRankToBand(measuredRank, band)] ?? 'mid'
}

// ===== Compute capabilities =====

function computeCapabilities(signals: DeviceSignals): DeviceCapabilities {
  const tier = deriveTier(signals)

  // Long-session: timestamps e mode preservados pra TELEMETRIA + prompt
  // de reload (#7 do roadmap pós-MVP). Capabilities de UI NÃO degradam
  // baseado em mode — mentir pro user ("animação some sem motivo") é
  // pior do que limpar memória ativamente. Cleanup roda em
  // long-session-cleaner.ts; prompt em LongSessionPrompt.tsx só dispara
  // se há evidência objetiva (heap pressure) — fallback do fallback.
  const longSessionThresholds = {
    warn: 3 * 60 * 60 * 1000,
    degrade: 6 * 60 * 60 * 1000,
    suggest: 8 * 60 * 60 * 1000,
  }
  const sessionAgeMs = Date.now() - signals.sessionStartedAt
  const longSessionMode: LongSessionMode =
    sessionAgeMs >= longSessionThresholds.suggest
      ? 'suggest'
      : sessionAgeMs >= longSessionThresholds.degrade
        ? 'degrade'
        : sessionAgeMs >= longSessionThresholds.warn
          ? 'warn'
          : 'normal'

  // Animation: atrelado a CPU (gpu correlato em TVs antigas).
  const animations: AnimationLevel =
    tier === 'vlow' ? 'off' : tier === 'low' ? 'minimal' : 'full'

  // Hero
  const autoplayHero = tier !== 'vlow'
  const heroIntervalMs = !autoplayHero
    ? 0
    : tier === 'low' || tier === 'mid'
      ? 8000
      : 6000

  // Player — preview HLS do hero da TV ao Vivo.
  //
  // O CPU passa pela MESMA BANDA de plataforma que o `data-tier` (`deriveAutoTier`),
  // então as duas decisões nunca divergem:
  //   - teto: uma webOS 3 / Tizen 3 (2017, Chr38) cujo bench dê um burst 'mid'
  //     NÃO liga autoplay HLS — a plataforma a trava em vlow. TV fraca segue
  //     protegida, que é o motivo de este gate existir.
  //   - piso: uma TV 2018-2020 (webOS 4-5, Chr53/68) NÃO é barrada por um bench
  //     de JS lento. O custo do preview numa TV é DECODE, e decode em TV é
  //     HARDWARE — é o mesmo decodificador que toca o canal em tela cheia. Medir
  //     JS single-thread pra decidir isso mede a coisa errada: era o que mantinha
  //     o preview desligado na LG 2019 (bench 50ms → 'low'), o bug reportado.
  //
  // RAM fica de fora de propósito (herdado): 2GB com CPU mid e rede fast aguenta
  // preview. Rede 'poor' desliga — constraint físico real, não heurística.
  const band = platformTierBand(signals.platform)
  const effectiveCpuTier = clampTierToBand(signals.cpu.tier, band)
  const livePreviewEnabled =
    effectiveCpuTier !== 'vlow' &&
    effectiveCpuTier !== 'low' &&
    signals.network.class !== 'poor'

  // Native-TS: capacidade de HARDWARE da plataforma (webOS gen 4-5 provado),
  // independente do tier de perf e do override do Settings — por isso sai do
  // `signals.platform` cru, não do `tier`. Ligar/desligar aqui NÃO muda a UI
  // de perf; muda só QUAL player toca o LIVE (mpegts.js vs `<video>` nativo).
  const nativeTsPlayback = canPlayNativeTs(signals.platform)

  // Prefetch de filme na ficha: o override do A/B vence; sem ele, a tabela por
  // plataforma (hoje `false` em todas). Sai do `signals.platform` cru, não do
  // tier: é custo de REDE no painel do provedor, não perf de UI.
  const vodPrefetch = readVodPrefetchOverride() ?? canPrefetchVod(signals.platform)

  // Lazy mount episódios — depende de RAM + storage (não só CPU)
  // Storage tight + low RAM = janela conservadora (pouco DOM montado).
  //
  // **Sentinel finito grande pra "ilimitado"** em vez de Infinity:
  // matemática segura (Math.min, comparação intermediária) + funciona
  // em Array(N) e setTimeout sem behavior weird.
  //
  // Em mid+ expandStep também é grande — séries longas (novelas BR com
  // 200+ eps, animes com 500+) precisam expandir além do initial,
  // mesmo em mid. Antes era 0 = trunca em 80, bug de produto.
  const UNBOUNDED = 1_000_000 // catálogos reais nunca chegam perto
  let episodeInitialWindow: number
  let episodeExpandStep: number
  if (tier === 'vlow') {
    episodeInitialWindow = 20
    episodeExpandStep = 40
  } else if (tier === 'low') {
    episodeInitialWindow = 40
    episodeExpandStep = 80
  } else if (tier === 'mid') {
    episodeInitialWindow = 80
    episodeExpandStep = 160 // cobre novelas/animes longos
  } else {
    episodeInitialWindow = UNBOUNDED
    episodeExpandStep = 0 // já tudo montado
  }

  // Catalog page size (D.0 do Catálogo v1) — proporcional ao tier.
  // vlow Tizen 2017 (~256MB RAM): 500 items × ~500 bytes JSON = ~250KB
  // descomprimido, parse < 100ms. low: 1000. mid: 2000. high/ultra: 5000.
  // Cliente busca próxima fatia quando user rola perto do fim do grid.
  const catalogPageSize =
    tier === 'vlow'
      ? 500
      : tier === 'low'
        ? 1000
        : tier === 'mid'
          ? 2000
          : 5000

  // Overscan da lista virtual da TV ao Vivo (perf/tv-live-nav-virtualize):
  // linhas EXTRA além do mínimo que o D-pad exige (foco + vizinhos). É ADITIVO
  // — o <LiveGuide> soma isto ao back/forward já medido, só ALARGANDO a janela;
  // nunca reduz abaixo do piso, então o invariante coberto por liveWindow.test.ts
  // (a janela sempre contém o foco e seus vizinhos) continua valendo. vlow/low
  // ficam em 0: memória apertada e o comportamento já validado na rc11 NÃO muda.
  const liveListOverscan = tier === 'vlow' || tier === 'low' ? 0 : tier === 'mid' ? 1 : 2

  // Network behavior — vlow/low não tolera retry (multiplica latência)
  const isWeak = tier === 'vlow' || tier === 'low'
  const retryCount: RetryCount = isWeak ? 0 : 1
  const retryDelayMs = 1500
  const fetchTimeoutMs = 15000

  // RQ gcTime — vlow precisa liberar memória mais rápido
  const rqGcTimeMs =
    tier === 'vlow'
      ? 2 * 60 * 1000
      : tier === 'low'
        ? 3 * 60 * 1000
        : tier === 'ultra'
          ? 10 * 60 * 1000
          : 5 * 60 * 1000

  // EPG polling — vlow desliga (CPU gasta), low intervalo maior
  const epgRefreshIntervalMs =
    tier === 'vlow' ? 0 : tier === 'low' ? 60000 : 30000

  // Catálogo client-side (#8) — base 30min match com worker backend.
  // Network 'poor' estende pra 60min: polling agressivo pode roubar
  // banda de streaming em Wi-Fi ruim, e snapshot é 1.5MB+ no pior caso.
  // Princípio long-session: NÃO degradamos por mode (cleanup ativo
  // cobre), mas bandwidth class é constraint físico real.
  const catalogRefreshIntervalMs =
    signals.network.class === 'poor' ? 60 * 60 * 1000 : 30 * 60 * 1000

  // SW image cache — usa storage signal QUANDO disponível (mais preciso),
  // tier conceitual como fallback. vlow Tizen 2017 storage é apenas ~50MB;
  // ultra PC pode ser 1GB+.
  //
  // **swImageCacheSuffix segue a MESMA fonte que decide maxEntries** —
  // se storage tier diz 'vlow' (50MB quota), suffix vira 'storage-vlow'.
  // Sem isso, suffix ficava em tier conceitual ('ultra' por CPU+RAM)
  // mas maxEntries=500 — incoerente em debug. Sufixo `storage-` evita
  // conflito com sufixos antigos baseados em CPU tier.
  let swImageMaxEntries: number
  let swImageCacheSuffix: string
  if (signals.storage.tier !== 'unknown') {
    switch (signals.storage.tier) {
      case 'vlow':
        swImageMaxEntries = 500
        break
      case 'low':
        swImageMaxEntries = 1500
        break
      case 'mid':
        swImageMaxEntries = 3000
        break
      case 'high':
        swImageMaxEntries = 5000
        break
    }
    swImageCacheSuffix = `storage-${signals.storage.tier}`
  } else {
    // Fallback baseado em tier conceitual
    swImageMaxEntries =
      tier === 'vlow'
        ? 500
        : tier === 'low'
          ? 1500
          : tier === 'mid'
            ? 3000
            : tier === 'high'
              ? 5000
              : 8000
    swImageCacheSuffix = tier
  }

  // Imagens — combina viewport + features
  // Posters/cards em qualquer TV grande continuam pequenos visualmente
  const imagePreset: ImagePreset =
    signals.viewport.tier === 'sd'
      ? 'small'
      : signals.viewport.tier === 'hd'
        ? 'small'
        : 'medium'

  // Backdrop — viewport tier mapeia direto
  const backdropPreset: BackdropPreset = signals.viewport.tier

  // Format preference — AVIF > WebP > JPEG
  const preferredImageFormat: PreferredImageFormat = signals.features.avif
    ? 'avif'
    : signals.features.webp
      ? 'webp'
      : 'jpeg'

  // Prefetch — vlow=1 (não saturar), mid=2, high+=4
  const prefetchConcurrency: PrefetchConcurrency = isWeak
    ? 1
    : tier === 'mid'
      ? 2
      : 4

  // Default player quality — auto via player; override em vlow/low pra evitar
  // que player escolha 4K em rede ruim
  const defaultPlayerQuality: ChannelQuality | 'auto' =
    tier === 'vlow' ? 'SD' : tier === 'low' ? 'HD' : 'auto'

  return {
    animations,
    autoplayHero,
    heroIntervalMs,
    livePreviewEnabled,
    defaultPlayerQuality,
    nativeTsPlayback,
    vodPrefetch,
    episodeInitialWindow,
    episodeExpandStep,
    catalogPageSize,
    liveListOverscan,
    retryCount,
    retryDelayMs,
    fetchTimeoutMs,
    rqGcTimeMs,
    epgRefreshIntervalMs,
    catalogRefreshIntervalMs,
    swImageMaxEntries,
    swImageCacheSuffix,
    imagePreset,
    backdropPreset,
    preferredImageFormat,
    prefetchConcurrency,
    longSessionThresholds,
    longSessionMode,
    cpuTier: tier,
  }
}

// ===== API pública =====

let cachedCapabilities: DeviceCapabilities | null = null
const subscribers = new Set<(caps: DeviceCapabilities) => void>()

/** Lê capabilities. Cacheado — recompute via refreshCapabilities(). */
export function getCapabilities(): DeviceCapabilities {
  if (cachedCapabilities) return cachedCapabilities
  cachedCapabilities = computeCapabilities(getSignals())
  return cachedCapabilities
}

/** Recomputa capabilities (signals mudaram: rede oscilou, override mudou).
 *  Notifica subscribers. Itera cópia pra defesa contra mutação durante
 *  callback (subscriber se desinscrevendo no próprio cb). */
export function refreshCapabilities(): void {
  cachedCapabilities = computeCapabilities(getSignals())
  for (const cb of [...subscribers]) cb(cachedCapabilities)
}

/** Subscribe pra mudanças de capabilities. Retorna unsubscribe. */
export function subscribeCapabilities(
  cb: (caps: DeviceCapabilities) => void,
): () => void {
  subscribers.add(cb)
  return () => subscribers.delete(cb)
}

/** Lê uma capability específica. Atalho semântico — caller declara o que
 *  precisa, não toda a struct. Type-safe via keyof. */
export function getCapability<K extends keyof DeviceCapabilities>(
  key: K,
): DeviceCapabilities[K] {
  return getCapabilities()[key]
}

// ===== Glue automático com signals =====
//
// Capabilities reagem a 2 eventos externos:
// 1. `initSignals` resolve (storage chega async pós-boot).
// 2. Network signal muda (rede oscila durante sessão).
//
// Sem este glue, caller chamar `getCapabilities()` antes de init terminar
// pega fallback síncrono congelado; e mudanças de rede não atualizam
// retryCount/defaultPlayerQuality em UI ativa.
//
// Listeners auto-attach na primeira import do módulo. Ordem garantida
// porque ESM avalia imports antes de export — caller ao chamar
// `getCapabilities()` já tem subscriptions ativas.
subscribeSignalsRefresh(refreshCapabilities)
subscribeNetwork(() => {
  // Network mudou — atualiza signals + recomputa caps
  refreshNetworkSignal()
})

// ===== Test helpers (NÃO usar em runtime) =====
//
// Vitest precisa injetar signals controlados pra cobrir matriz tier
// completa (vlow → ultra) sem depender do ambiente real do CI.

/** Injeta signals mockados e força recompute. Retorna função pra
 *  restaurar comportamento real. NÃO usar em runtime. */
export function __injectSignalsForTests(mockedSignals: DeviceSignals): () => void {
  const original = computeCapabilities
  // Substitui temporariamente — captura via closure no cachedCapabilities.
  cachedCapabilities = original(mockedSignals)
  for (const cb of [...subscribers]) cb(cachedCapabilities)
  return () => {
    // Restaura: força recompute com signals reais
    cachedCapabilities = null
    refreshCapabilities()
  }
}

/** Reset interno pra isolation entre tests. */
export function __resetCapabilitiesForTests(): void {
  cachedCapabilities = null
  subscribers.clear()
}
